import React from "react";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { PostHogConfig } from "posthog-js";
import { useLocation } from "react-router";
import { useAuth } from "./Auth";
import { getPosthogConfig } from "./posthogConfig";

export function WithPostHog({ children }: React.PropsWithChildren) {
  const posthog = getPosthogConfig();
  if (!posthog) {
    return children;
  }
  const options: Partial<PostHogConfig> = {
    api_host: posthog.host,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: false,
    debug: import.meta.env.DEV,
    session_recording: {
      recordCrossOriginIframes: true,
    },
    loaded: (client) => {
      client.register({ ENVIRONMENT: import.meta.env.MODE });
    },
  };
  return (
    <PostHogProvider apiKey={posthog.key} options={options}>
      {children}
    </PostHogProvider>
  );
}

export function PostHogInstrumentation(props: React.PropsWithChildren) {
  const posthog = usePostHog();
  const auth = useAuth();
  const location = useLocation();

  const hasIdentified = React.useRef(false);

  React.useEffect(() => {
    if (!auth.isHydrated) {
      return;
    }
    if (!auth.isAuthenticated) {
      posthog.capture("screenleave", {
        url: window.location.pathname,
      });
      posthog?.reset();
      hasIdentified.current = false;
      return;
    }
    const user = auth.user;
    posthog?.identify(
      user.id,
      {
        emails: user.identities.map((i) => i.email),
        is_root: user.is_root,
        name: user.name,
      },
      {
        organization: {
          id: user.organization_id,
          name: user.organization.name,
        },
      },
    );

    posthog?.group("organization", user.organization.id, { name: user.organization.name });
    hasIdentified.current = true;
  }, [posthog, auth.isHydrated, auth.isAuthenticated, auth.user]);

  React.useEffect(() => {
    if (!hasIdentified.current) {
      return;
    }
    const pathname = location.pathname;
    posthog.capture("screenview", {
      url: pathname,
    });
    return () => {
      posthog.capture("screenleave", {
        url: pathname,
      });
    };
  }, [posthog, location.pathname, auth.isAuthenticated]);

  return props.children;
}
